import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Store, Tag, PackageOpen } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListingCard } from "@/components/market/MarketUI";
import { Loader, EmptyState } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALL = "all";

export default function Marketplace() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [data, setData] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [minP, setMinP] = useState(params.get("min_price") || "");
  const [maxP, setMaxP] = useState(params.get("max_price") || "");
  const kind = params.get("kind") || "";

  useEffect(() => { api.get("/market/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => {
    setData(null);
    const p = Object.fromEntries(params.entries());
    api.get("/market/listings", { params: p }).then((r) => setData(r.data)).catch(() => setData({ items: [], pages: 1 }));
  }, [params]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && v !== ALL) next.set(k, v); else next.delete(k);
    if (k === "kind") next.delete("category");
    if (k !== "page") next.delete("page");
    setParams(next);
  };
  const applyText = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    [["q", q], ["city", city], ["min_price", minP], ["max_price", maxP]].forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    next.delete("page");
    setParams(next);
  };
  const clear = () => { setQ(""); setCity(""); setMinP(""); setMaxP(""); setParams(new URLSearchParams()); };
  const page = parseInt(params.get("page") || "1", 10);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="eyebrow text-primary flex items-center gap-2"><Store className="h-4 w-4" /> Mercado PaleteLab</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight">Compre de quem faz.</h1>
          <p className="mt-2 text-muted-foreground max-w-xl">Móveis prontos feitos por makers e materiais de reúso, com pagamento seguro pelo site.</p>
        </div>
        <div className="flex gap-2">
          {user && <Button asChild variant="outline" className="rounded-full" data-testid="market-panel-link"><Link to="/mercado/painel">Minhas compras e vendas</Link></Button>}
          <Button asChild className="rounded-full gap-1.5" data-testid="market-sell-btn"><Link to={user ? "/mercado/anunciar" : "/entrar"}><Tag className="h-4 w-4" /> Vender</Link></Button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {[["", "Tudo"], ["movel", "Móveis prontos"], ["material", "Materiais e paletes"]].map(([v, l]) => (
          <button key={l} data-testid={`market-kind-${v || "all"}`} onClick={() => setParam("kind", v)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${kind === v ? "bg-foreground text-background" : "bg-secondary hover:bg-secondary/70"}`}>{l}</button>
        ))}
      </div>

      <form onSubmit={applyText} className="mt-4 grid gap-3 rounded-2xl border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-6">
        <div className="relative lg:col-span-2">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="market-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar anúncios..." className="pl-9 rounded-xl" />
        </div>
        <Select value={params.get("category") || ALL} onValueChange={(v) => setParam("category", v)}>
          <SelectTrigger data-testid="market-category-select" className="rounded-xl"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todas as categorias</SelectItem>
            {cats.filter((c) => !kind || c.kind === kind).map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={params.get("fulfillment") || ALL} onValueChange={(v) => setParam("fulfillment", v)}>
          <SelectTrigger data-testid="market-fulfillment-select" className="rounded-xl"><SelectValue placeholder="Entrega" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Entrega ou retirada</SelectItem>
            <SelectItem value="entrega">Com entrega</SelectItem>
            <SelectItem value="retirada">Retirada no local</SelectItem>
          </SelectContent>
        </Select>
        <Input data-testid="market-city-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Cidade" className="rounded-xl" />
        <Select value={params.get("sort") || "recent"} onValueChange={(v) => setParam("sort", v)}>
          <SelectTrigger data-testid="market-sort-select" className="rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Mais recentes</SelectItem>
            <SelectItem value="price_asc">Menor preço</SelectItem>
            <SelectItem value="price_desc">Maior preço</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2 lg:col-span-2">
          <Input data-testid="market-min-price" inputMode="decimal" value={minP} onChange={(e) => setMinP(e.target.value.replace(/[^\d.]/g, ""))} placeholder="Preço mín. R$" className="rounded-xl" />
          <Input data-testid="market-max-price" inputMode="decimal" value={maxP} onChange={(e) => setMaxP(e.target.value.replace(/[^\d.]/g, ""))} placeholder="Preço máx. R$" className="rounded-xl" />
        </div>
        <div className="flex gap-2 lg:col-span-4 lg:justify-end">
          <Button type="button" variant="ghost" data-testid="market-clear-btn" onClick={clear} className="rounded-full">Limpar</Button>
          <Button type="submit" data-testid="market-apply-btn" className="rounded-full">Aplicar filtros</Button>
        </div>
      </form>

      <div className="mt-8">
        {data === null ? <Loader /> : data.items.length === 0 ? (
          <EmptyState icon={PackageOpen} title="Nenhum anúncio encontrado." subtitle="Tente outros filtros ou seja o primeiro a anunciar." />
        ) : (
          <>
            <p data-testid="market-total" className="mb-4 text-sm text-muted-foreground">{data.total} anúncio(s)</p>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {data.items.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
            </div>
            {data.pages > 1 && (
              <div className="mt-8 flex justify-center gap-2">
                <Button variant="outline" disabled={page <= 1} onClick={() => setParam("page", String(page - 1))} className="rounded-full" data-testid="market-prev-page">Anterior</Button>
                <span className="self-center text-sm">{page} / {data.pages}</span>
                <Button variant="outline" disabled={page >= data.pages} onClick={() => setParam("page", String(page + 1))} className="rounded-full" data-testid="market-next-page">Próxima</Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
